import { React, useState, useContext } from 'react'
import { MyContextProvider } from "../../../../../App";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getFormattedDate } from '../../../../../Custom/Custom'
import { GlobalLoading, showLoading } from 'react-global-loading';
import { jwtDecode } from 'jwt-decode';
import titlesData from '../../../../../titles.json';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import messages from '../.././../../../assets/Messages.json';
import { ToastContainer, toast } from 'react-toastify';
import { generateExcelIndexComparisonReport } from '../../ExportIIPExcel';
import iipTitleData from '../../../../../iip.json';

const parseNumber = (val) => {
    if (val === null || val === undefined || val === '') return null;
    const num = Number(val);
    return Number.isFinite(num) ? num : null;
};

const formatIndexValue = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const num = Number(val);
    return Number.isNaN(num) ? String(val) : num.toFixed(2);
};

/** Same formula as IndexReport: ((current - reference) / reference) * 100 */
const getVariationPercent = (currentIndexRaw, referenceIndexRaw) => {
    const current = parseNumber(currentIndexRaw);
    const reference = parseNumber(referenceIndexRaw);
    if (current === null || reference === null) return '';
    let variation = 0;
    if (reference === 0) variation = current === 0 ? 0 : 100;
    else variation = ((current - reference) / reference) * 100;
    if (Math.abs(variation) < 1e-9) variation = 0;
    return `${variation.toFixed(2)}%`;
};

const IndexMonthlyComparison = () => {
    const token = useContext(MyContextProvider).token;
    const decode_token = jwtDecode(token);
    const [isDivision] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const storedLanguage = localStorage.getItem('language');
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate());

    const getCurrentDateTime = () => {
        const now = new Date();
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const year = now.getFullYear();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const seconds = now.getSeconds().toString().padStart(2, '0');
        return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
    };

    const mapComparisonRows = (rows = [], sameMonthAndYear) =>
        rows.map((item) => {
            const current = item.currentYearValue;
            const previousMonth = item.previousMonthOfSameYearValue;
            const previousYear = item.previousYearValue;

            const percentPrevMonth =
                item.percentPreviousMonthOfSameYear ||
                getVariationPercent(current, previousMonth);
            const percentPrevYear =
                item.percentPrevious ||
                getVariationPercent(current, previousYear);

            if (sameMonthAndYear) {
                return [
                    item.nic2d ?? '',
                    formatIndexValue(current),
                    formatIndexValue(previousMonth),
                    percentPrevMonth,
                    formatIndexValue(previousYear),
                    percentPrevYear,
                ];
            }

            return [
                item.nic2d ?? '',
                formatIndexValue(current),
                formatIndexValue(previousYear),
                percentPrevYear,
            ];
        });

    const handleGenerateExcel = () => {
        if (!startDate || !endDate) {
            toast.error('Please select the date range', messages.COLOR_RED);
            return;
        }

        showLoading(true);
        const payload = {
            fromDate: getFormattedDate(startDate),
            toDate: getFormattedDate(endDate),
        };

        const sameMonthAndYear =
            startDate.getFullYear() === endDate.getFullYear() &&
            startDate.getMonth() === endDate.getMonth();

        const previousMonthDate = new Date(startDate);
        previousMonthDate.setMonth(startDate.getMonth() - 1);
        const previousMonthFormatted = getFormattedDate(previousMonthDate).split('-')[0];

        const previousYearDate = new Date(startDate);
        previousYearDate.setFullYear(startDate.getFullYear() - 1);
        const previousYearFormatted = getFormattedDate(previousYearDate).split('-')[0];

        const headerEnglishColumns = sameMonthAndYear
            ? [
                'NIC 2d',
                `index${payload.fromDate}`,
                `Previous month Index(${previousMonthFormatted})`,
                'variation with Previous Month',
                `Previous year Index(${previousYearFormatted})`,
                'variation with Previous Year',
            ]
            : ['NIC 2d', 'Current Year', 'Previous Year', 'Percent Previous'];

        const fourthRowText = sameMonthAndYear
            ? ` (${payload.fromDate}) `
            : ` (${payload.fromDate}-${payload.toDate}) `;

        const excelFileName = sameMonthAndYear
            ? `IndexComparisonGroupIndexReport_${payload.fromDate}`
            : `IndexComparisonGroupIndexReport(${payload.fromDate}-${payload.toDate})`;

        axios
            .post(`${API_URL}api/iipReports/HQReports/ComparisonReport`, payload, config)
            .then((response) => {
                if (!response.data?.status) {
                    toast.info(response.data?.message || 'No data found.');
                    return;
                }

                const generalIndexReportData = response.data?.data?.iipResultsDistrict || [];
                if (!generalIndexReportData.length) {
                    toast.info('No data found for the specified parameters.');
                    return;
                }

                const excelObjectData = {
                    fileName: excelFileName,
                    reportType: excelFileName,
                    fileType: 'xls',
                    sheetName: 'IIPComparisonReport',
                    firstRowMergeCells: sameMonthAndYear ? 'A1:F1' : 'A1:D1',
                    firstRowExportDate: getCurrentDateTime(),
                    firstRowCellNumber: 12,
                    secondRowMergeCells: sameMonthAndYear ? 'A2:F2' : 'A2:D2',
                    secondRowExportData: 'COMPUTATION OF INDUSTRIAL PRODUCTION INDEX  U.P ',
                    thirdRowMergeCells: sameMonthAndYear ? 'A3:F3' : 'A3:D3',
                    thirdRowText: 'Monthly Comparison',
                    fourthRowMergeCells: sameMonthAndYear ? 'A4:F4' : 'A4:D4',
                    fourthRowText,
                    fromDate: payload.fromDate,
                    toDate: payload.toDate,
                    headerEnglishColumns,
                    headerHindiColumns: [],
                    // Same colors as IndexReport previous month / previous year
                    hasMonthlyIndexComparison: sameMonthAndYear,
                    previousMonthHighlightColor: 'ADD8E6',
                    previousYearHighlightColor: 'FFE4B5',
                    excelRowData: mapComparisonRows(generalIndexReportData, sameMonthAndYear),
                };

                generateExcelIndexComparisonReport(excelObjectData);
            })
            .catch((error) => {
                console.error('API Error:', error);
                toast.error(
                    error?.response?.data?.message || 'Failed to generate comparison report.',
                    messages.COLOR_RED
                );
            })
            .finally(() => {
                showLoading(false);
            });
    };

    return (
        <>
            <GlobalLoading />
            <div className="container-fluid py-3">
                <div className="card mx-3 mb-3">
                    <div className="card-body pb-1">
                        <div className="row">
                            <div className="col-12">
                                <h4 className="page_heading">
                                    <div dangerouslySetInnerHTML={{ __html: storedLanguage === 'hi' ? iipTitleData.hi.INDEX_MONTHLY_COMPARRISON : iipTitleData.en.INDEX_MONTHLY_COMPARRISON }} />
                                </h4>
                            </div>
                        </div>
                        <div className="row align-items-end">
                            {isDivision && (
                                <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12 mb-3">
                                    <label className="form-label">{storedLanguage === 'hi' ? titlesData.hi.DIVISION : titlesData.en.DIVISION}</label>
                                    <input type="text" className="form-control" disabled value={decode_token.id.LOCATION_NAME} />
                                </div>
                            )}

                            <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12 mb-3">
                                <label className="form-label">From Date</label>
                                <DatePicker
                                    className="form-control calendar_icon"
                                    wrapperClassName="w-100 d-block"
                                    selected={startDate}
                                    onChange={(e) => { setStartDate(e) }}
                                    dateFormat="MMM yyyy"
                                    showMonthYearPicker
                                    placeholderText="Select month"
                                    maxDate={maxDate}
                                />
                            </div>

                            <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12 mb-3">
                                <label className="form-label">To Date</label>
                                <DatePicker
                                    className="form-control calendar_icon"
                                    wrapperClassName="w-100 d-block"
                                    selected={endDate}
                                    onChange={(e) => { setEndDate(e) }}
                                    dateFormat="MMM yyyy"
                                    showMonthYearPicker
                                    placeholderText="Select month"
                                    maxDate={maxDate}
                                />
                            </div>

                            <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12 mb-3">
                                <button onClick={handleGenerateExcel} className="btn btn_orange">Get Report</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </>
    );
};

export default IndexMonthlyComparison;
